import { db, doc, onSnapshot, secureGetDoc, secureSetDoc } from '../firebase';

export interface PaymentMethodsSettings {
  cod: boolean;   // เก็บเงินปลายทาง (Cash on Delivery)
  qr: boolean;    // คิวอาร์เพย์เม้นท์ (QR Payment / PromptPay)
  card: boolean;  // บัตรเครดิต/บัตรเดบิต (Credit/Debit Card)
}

export const DEFAULT_PAYMENT_METHODS: PaymentMethodsSettings = {
  cod: true,
  qr: true,
  card: false
};

const STORAGE_KEY = 'payment_methods_settings';
const EVENT_KEY = 'payment-settings-updated';

/**
 * Get current payment settings from local storage or defaults synchronously
 */
export function getCachedPaymentSettings(): PaymentMethodsSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        cod: typeof parsed.cod === 'boolean' ? parsed.cod : DEFAULT_PAYMENT_METHODS.cod,
        qr: typeof parsed.qr === 'boolean' ? parsed.qr : DEFAULT_PAYMENT_METHODS.qr,
        card: typeof parsed.card === 'boolean' ? parsed.card : DEFAULT_PAYMENT_METHODS.card
      };
    }
  } catch (e) {
    console.error('Error parsing payment settings from localStorage:', e);
  }
  return { ...DEFAULT_PAYMENT_METHODS };
}

/**
 * Fetch latest payment settings from Firestore with API fallback
 */
export async function fetchPaymentSettings(): Promise<PaymentMethodsSettings> {
  try {
    // 1. Try Firestore site_settings/payments
    const snap = await secureGetDoc(doc(db, 'site_settings', 'payments'), 'site_settings/payments');
    if (snap && snap.exists()) {
      const data = snap.data() as any;
      if (data && data.paymentMethods) {
        const settings: PaymentMethodsSettings = {
          cod: Boolean(data.paymentMethods.cod),
          qr: Boolean(data.paymentMethods.qr),
          card: Boolean(data.paymentMethods.card)
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        return settings;
      }
    }

    // 2. Try Backend API fallback
    const res = await fetch('/api/settings/payments');
    if (res.ok) {
      const apiData = await res.json();
      if (apiData && apiData.paymentMethods) {
        const settings: PaymentMethodsSettings = {
          cod: Boolean(apiData.paymentMethods.cod),
          qr: Boolean(apiData.paymentMethods.qr),
          card: Boolean(apiData.paymentMethods.card)
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        return settings;
      }
    }
  } catch (err) {
    console.error('Failed to fetch payment settings:', err);
  }

  return getCachedPaymentSettings();
}

/**
 * Save updated payment settings across Firestore, Backend API, and localStorage
 */
export async function savePaymentSettings(settings: PaymentMethodsSettings): Promise<boolean> {
  try {
    // 1. Save to localStorage immediately
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));

    // 2. Broadcast to other components in the same window
    window.dispatchEvent(new CustomEvent(EVENT_KEY, { detail: settings }));

    // 3. Save to Firestore
    await secureSetDoc(
      doc(db, 'site_settings', 'payments'),
      {
        paymentMethods: settings,
        updatedAt: new Date().toISOString()
      },
      'site_settings/payments'
    );

    // 4. Save to Backend API
    await fetch('/api/settings/payments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentMethods: settings })
    }).catch(err => console.warn('API sync warning:', err));

    return true;
  } catch (err) {
    console.error('Failed to save payment settings:', err);
    return false;
  }
}

/**
 * Real-time listener for Payment Settings across tabs, Firestore, and window events
 */
export function subscribePaymentSettings(onUpdate: (settings: PaymentMethodsSettings) => void): () => void {
  // Call immediately with cached settings
  onUpdate(getCachedPaymentSettings());

  // Listen to Firestore real-time snapshot
  let unsubFirestore: (() => void) | null = null;
  try {
    unsubFirestore = onSnapshot(
      doc(db, 'site_settings', 'payments'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as any;
          if (data && data.paymentMethods) {
            const settings: PaymentMethodsSettings = {
              cod: Boolean(data.paymentMethods.cod),
              qr: Boolean(data.paymentMethods.qr),
              card: Boolean(data.paymentMethods.card)
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
            onUpdate(settings);
          }
        }
      },
      (error) => {
        console.warn('Firestore onSnapshot error on site_settings/payments:', error);
      }
    );
  } catch (e) {
    console.warn('Could not bind onSnapshot:', e);
  }

  // Listen to localStorage cross-tab changes
  const handleStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try {
        const parsed = JSON.parse(e.newValue);
        onUpdate({
          cod: Boolean(parsed.cod),
          qr: Boolean(parsed.qr),
          card: Boolean(parsed.card)
        });
      } catch (err) {}
    }
  };

  // Listen to in-app custom events
  const handleCustomEvent = (e: Event) => {
    const customEvent = e as CustomEvent<PaymentMethodsSettings>;
    if (customEvent.detail) {
      onUpdate(customEvent.detail);
    } else {
      onUpdate(getCachedPaymentSettings());
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(EVENT_KEY, handleCustomEvent);

  // Also trigger an async fetch to make sure latest remote data is fetched
  fetchPaymentSettings().then(latest => {
    onUpdate(latest);
  });

  return () => {
    if (unsubFirestore) unsubFirestore();
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(EVENT_KEY, handleCustomEvent);
  };
}
